// @ts-nocheck
import { Button, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Spin, Upload, message } from 'antd';

import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get, postFormData, putFormData } from '../../../../api/requests';
import { UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { queryClient } from '../../../../config';

type Customer = { id: number; name: string };
type Responsible = { id: number; name: string };
type Region = { id: number; name: string };
type Developer = { id: number; name: string };
type ProjectStatus = { id: number; name: string };

type ProjectFormProps = {
  type: string;
  id?: number | null;
  idCustomer?: number | null;
  add: (v: CreateContact) => void;
  update: (v: Contact) => void;
};

const { Option } = Select;

const ProjectForm: React.FC<ProjectFormProps> = (props) => {
  const { type, id, idCustomer, setModalIsVisible } = props;
  const [form] = Form.useForm();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isLoading: isLoadingProject, data } = useQuery<any>(
    [type, id],
    () => (id ? get<any>('/projects/' + id).then((e) => e.data) : ''),
    { enabled: !!id && type !== 'add' }
  );
  const { isLoading: isLoadingCustomers, data: customersget } = useQuery<Customer[]>(
    ['cliente'],
    () => get<Customer[]>('/customers').then((e) => e.data)
  );
  const { data: responsibleget } = useQuery<Responsible[]>(
    ['responsibleget'],
    () => get<Responsible[]>('/responsibles').then((e) => e.data)
  );
  const { data: regionsget } = useQuery<Region[]>(
    ['regionsget'],
    () => get<Region[]>('/regions').then((e) => e.data)
  );
  const { data: developersget } = useQuery<Developer[]>(
    ['developersget'],
    () => get<Developer[]>('/developers').then((e) => e.data)
  );
  const { data: statuses } = useQuery<ProjectStatus[]>(
    ['project-status'],
    () => get<ProjectStatus[]>('/projects/status').then((e) => e.data)
  );

  useEffect(() => {
    if (customersget) setCustomers([...customersget].sort((a, b) => a.name.localeCompare(b.name)));
  }, [customersget]);

  useEffect(() => {
    if (data && type !== 'add' && id && !isLoadingProject) {
      const safeStart = data?.startDate ? dayjs(data.startDate) : null;
      const safeEnd = data?.endDate ? dayjs(data.endDate) : null;
      const safePhasesDates = Array.isArray(data?.phasesDates)
        ? data.phasesDates.map((d: any) => (d ? dayjs(d) : null))
        : [];

      const initialFiles = Array.isArray(data?.documents)
        ? data.documents.map((doc: any, idx: number) => ({
            uid: `doc-${doc.id ?? idx}`,
            name: doc.fileName ?? `doc-${idx}`,
            status: 'done',
            url: doc.fileLocation,      // usamos la URL firmada como string
            __docId: doc.id ?? 0,
            __isExisting: true,
          }))
        : [];
      setFileList(initialFiles);

      form.setFieldsValue({
        customerId: data?.customerId ?? null,
        responsibleId: data?.responsibleId ?? null,
        regionId: data?.regionId ?? null,
        name: data?.name ?? '',
        description: data?.description ?? '',
        totalPhases: data?.totalPhases ?? null,
        currentPhase: data?.currentPhase ?? null,
        startDate: safeStart,
        endDate: safeEnd,
        status: data?.status ?? null,
        comments: data?.comments ?? '',
        cost: data?.cost ?? null,
        totalCost: data?.totalCost ?? null,
        developers: data?.developers ?? [],
        phasesDates: safePhasesDates,
      });
    }
  }, [form, data, id, type, isLoadingProject]);

  useEffect(() => {
    if (idCustomer) form.setFieldsValue({ customerId: idCustomer });
  }, [idCustomer, form]);

  const onUploadChange = ({ fileList: fl }: any) => {
    setFiles(fl.map(f => f.originFileObj))
    setFileList(fl)
  };
  const handleRefresh = () => queryClient.invalidateQueries(`/projects`);

  // nuevo -> convertir File a dataURL (string)
  const fileToDataURL = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = reject;
      r.readAsDataURL(file);
    });

    const onFinish = async (values: any) => {
    setIsSubmitting(true);

    // ✅ VALIDATION: phasesDates required + must match totalPhases
    const phasesDates = Array.isArray(values?.phasesDates)
      ? values.phasesDates.filter(Boolean)
      : [];
    const total = Number(values?.totalPhases ?? 0);

    if (phasesDates.length === 0) {
       message.error('Ingrese al menos una fecha por fase.');
      form.setFields([
        { name: 'phasesDates', errors: ['Ingrese al menos una fecha por fase.'] },
      ]);
      setIsSubmitting(false);
      return;
    }

    if (total > 0 && phasesDates.length !== total) {
      message.error('El número de fechas por fase debe ser igual a "Total de Fases".');
      form.setFields([
        { name: 'phasesDates', errors: ['El número de fechas por fase debe ser igual a "Total de Fases".'] },
      ]);
      setIsSubmitting(false);
      return;
    }
    // ✅ END VALIDATION

    try {
      const fd = new FormData();
      fd.append('Name', values.name ?? '');
      if (values.description != null) fd.append('Description', values.description);
      if (values.customerId != null) fd.append('CustomerId', String(values.customerId));
      if (values.responsibleId != null) fd.append('ResponsibleId', String(values.responsibleId));
      if (values.regionId != null) fd.append('RegionId', String(values.regionId));
      if (values.status != null) fd.append('Status', String(values.status));
      if (values.totalPhases != null) fd.append('TotalPhases', String(values.totalPhases));
      if (values.currentPhase != null) fd.append('CurrentPhase', String(values.currentPhase));
      if (values.cost != null) fd.append('Cost', String(values.cost));
      if (values.totalCost != null) fd.append('TotalCost', String(values.totalCost));
      if (values.comments != null) fd.append('Comments', values.comments);
      if (values.startDate) fd.append('StartDate', values.startDate.toISOString());
      if (values.endDate) fd.append('EndDate', values.endDate.toISOString());
      if (Array.isArray(values.developers)) values.developers.forEach((d: number) => fd.append('Developers', String(d)));
      if (Array.isArray(values.phasesDates))
        values.phasesDates.filter(Boolean).forEach((d: any) => fd.append('PhasesDates', d.toISOString()));

      for (let i = 0; i < fileList.length; i++) {
        const item = fileList[i];
        const file = files[i];
        if (item.__docId) {
          fd.append(`Documents[${i}].Id`, item.__docId);
        } else {
          fd.append(`Documents[${i}].File`, file);
        }
        fd.append(`Documents[${i}].FileName`, item.name);
      }

      if (type === 'add') {
        await postFormData('/projects', fd, true);
      } else {
        fd.append('id', id);
        await putFormData(`/projects/${id}`, fd, true);
      }

      setModalIsVisible?.(false);
      handleRefresh();
    } finally {
      setIsSubmitting(false);
    }
  };


  const disabledAll = type === 'watch';

  const responsibleOptions = useMemo(
    () => (responsibleget ?? []).map((r) => <Option key={r.id} value={r.id}>{r.name}</Option>),
    [responsibleget]
  );
  const regionOptions = useMemo(
    () => (regionsget ?? []).map((r) => <Option key={r.id} value={r.id}>{r.name}</Option>),
    [regionsget]
  );
  const developerOptions = useMemo(
    () => (developersget ?? []).map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>),
    [developersget]
  );
  const statusOptions = useMemo(
    () => (statuses ?? []).map((s) => <Option key={s.id ?? s.value} value={s.id ?? s.value}>{s.name}</Option>),
    [statuses]
  );

  return (
    <Spin spinning={isLoadingProject && type !== 'add'} tip={<p>Cargando...</p>}>
<Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 1200 }} scrollToFirstError>        <Divider />
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item label="Proyecto:" name="name" rules={[{ required: true, message: 'Por favor ingrese un nombre' }]}>
              <Input placeholder="Nombre del proyecto" disabled={disabledAll} />
            </Form.Item>
            <Form.Item label="Descripción:" name="description" rules={[{ required: true, message: 'Ingrese descripción' }]}>
              <Input placeholder="Descripción breve" disabled={disabledAll} />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Cliente:" name="customerId" rules={[{ required: true, message: 'Seleccione un cliente' }]}>
                  <Select
                    disabled={disabledAll || !!idCustomer || isLoadingCustomers}
                    placeholder="Selecciona cliente"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(i, o) => (o?.children as string).toLowerCase().includes(i.toLowerCase())}
                  >
                    {customers?.map((s) => (
                      <Option key={s.id} value={s.id}>{s.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Responsable:" name="responsibleId" rules={[{ required: true, message: 'Seleccione responsable' }]}>
                  <Select disabled={disabledAll} placeholder="Selecciona responsable" showSearch>
                    {responsibleOptions}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Región:" name="regionId" rules={[{ required: true, message: 'Seleccione región' }]}>
                  <Select disabled={disabledAll} placeholder="Selecciona región" showSearch>
                    {regionOptions}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Estatus:" name="status" rules={[{ required: true, message: 'Seleccione estatus' }]}>
                  <Select disabled={disabledAll} placeholder="Selecciona estatus" showSearch>
                    {statusOptions}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Total de Fases:" name="totalPhases" rules={[{ required: true, message: 'Ingrese total de fases' }]}>
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="0" disabled={disabledAll} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Fase Actual:"
                  name="currentPhase"
                  rules={[
                    { required: true, message: 'Ingrese fase actual' },
                    {
                      validator: (_, value) => {
                        const total = form.getFieldValue('totalPhases');
                        if (value == null || total == null) return Promise.resolve();
                        const ok = Number(value) >= 1 && Number(value) <= Number(total);
                        return ok ? Promise.resolve() : Promise.reject(new Error('La fase actual debe estar en el rango de total de fases.'));
                      },
                    },
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="0" disabled={disabledAll} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Fecha de inicio:" name="startDate" rules={[{ required: true, message: 'Seleccione fecha de inicio' }]}>
                  <DatePicker style={{ width: '100%' }} disabled={disabledAll} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Fecha de término:" name="endDate" rules={[{ required: true, message: 'Seleccione fecha de término' }]}>
                  <DatePicker style={{ width: '100%' }} disabled={disabledAll} />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col xs={24} md={12}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Costo:" name="cost" rules={[{ required: true, message: 'Ingrese costo' }]}>
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="0" disabled={disabledAll}
                    formatter={(v) => (v != null ? `$ ${String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '')}
                    parser={(v) => (v ? v.replace(/\$\s?|(,*)/g, '') : '')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Costo Total:" name="totalCost" rules={[{ required: true, message: 'Ingrese costo total' }]}>
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="0" disabled={disabledAll}
                    formatter={(v) => (v != null ? `$ ${String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '')}
                    parser={(v) => (v ? v.replace(/\$\s?|(,*)/g, '') : '')}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Developers:" name="developers">
              <Select mode="multiple" disabled={disabledAll} placeholder="Selecciona developers" showSearch>
                {developerOptions}
              </Select>
            </Form.Item>
            <Form.Item label="Comentarios:" name="comments" rules={[{ required: true, message: 'Ingrese comentarios' }]}>
              <TextArea style={{ height: 160 }} placeholder="Comentarios" disabled={disabledAll} />
            </Form.Item>
            <Form.Item label="Documentos (imágenes):" name="documents" >
              <Upload
                multiple
                listType="picture"
                fileList={fileList}
                beforeUpload={() => false}
                onChange={onUploadChange}
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                disabled={disabledAll}
              >
                {!disabledAll && <Button icon={<UploadOutlined />}>Agregar documentos</Button>}
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Form.Item label="Fechas por Fase:" rules={[{ required: true, message: 'Ingrese fechas por fase' }]}>
          <Form.List name="phasesDates" >
            {(fields, { add, remove }) => (
              <Row gutter={[12, 8]}>
                {fields.map((field, idx) => (
                  <Col xs={24} md={8} key={field.key}>
                    <Row gutter={8} align="middle">
                      <Col flex="auto">
                        <Form.Item {...field} name={[field.name]} rules={[{ required: true, message: 'Seleccione fecha' }]}>
                          <DatePicker style={{ width: '100%' }} disabled={disabledAll} placeholder={`Fase ${idx + 1}`} />
                        </Form.Item>
                      </Col>
                      <Col>
                        {!disabledAll && <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />}
                      </Col>
                    </Row>
                  </Col>
                ))}
                {!disabledAll && (
                  <Col span={24}>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                      Agregar fecha de fase
                    </Button>
                  </Col>
                )}
              </Row>
            )}
          </Form.List>
        </Form.Item>

        <Row justify="end">
          <Form.Item>
            <Button htmlType="submit" className="btn-add" disabled={disabledAll || isSubmitting}>
              Guardar
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </Spin>
  );
};

export default ProjectForm;
